import { getAccessToken, getScopeHeaders } from '../lib/auth-storage';

const API_BASE = import.meta.env.VITE_MARKETPLACESYNC_API ?? '/api/v1';

export type MarketplaceChannel = 'amazon_spapi' | 'flipkart' | 'myntra' | 'ajio' | 'shopify' | 'woocommerce';

export type PublicationJob = {
  id: string;
  listing_id: string;
  product_id: string;
  channel: MarketplaceChannel;
  action: string;
  status: string;
  retry_count: number;
  error_message?: string;
  created_at: string;
  updated_at: string;
};

export type SyncJob = {
  id: string;
  channel: MarketplaceChannel;
  sync_type: string;
  direction: string;
  status: string;
  items_processed: number;
  created_at: string;
};

export type Feed = {
  id: string;
  channel: MarketplaceChannel;
  feed_type: string;
  status: string;
  document_count: number;
  submission_id?: string;
  created_at: string;
};

export type Connector = {
  id: string;
  channel: MarketplaceChannel;
  name: string;
  enabled: boolean;
  health_status: string;
};

export type IntegrationDashboard = {
  queued_publications: number;
  running_publications: number;
  failed_publications: number;
  active_feeds: number;
  active_sync_jobs: number;
  dead_letter_count: number;
  connector_health?: Record<string, number>;
};

export type RetryTask = {
  id: string;
  job_id: string;
  job_type: string;
  status: string;
  retry_count: number;
  next_retry_at: string;
};

export type DeadLetterEntry = {
  id: string;
  job_id: string;
  job_type: string;
  error_message: string;
  created_at: string;
};

export type WebhookEvent = {
  id: string;
  channel: MarketplaceChannel;
  event_type: string;
  verified: boolean;
  processed: boolean;
  created_at: string;
};

function headers(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...getScopeHeaders(),
    ...(getAccessToken() ? { Authorization: `Bearer ${getAccessToken()}` } : {}),
  };
}

async function msFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: { ...headers(), ...(init?.headers as Record<string, string> | undefined) },
  });
  if (!res.ok) throw new Error(`MarketplaceSync ${path} failed: ${res.status} ${await res.text()}`);
  const body = await res.json();
  return (body.data ?? body) as T;
}

export const marketplacesyncApi = {
  dashboard: () => msFetch<IntegrationDashboard>('/publications/dashboard'),

  listPublications: (params?: { status?: string; channel?: string }) => {
    const q = new URLSearchParams(params as Record<string, string>).toString();
    return msFetch<PublicationJob[]>(`/publications${q ? `?${q}` : ''}`);
  },
  createPublication: (input: { listing_id: string; product_id: string; channel: MarketplaceChannel; action: string }) =>
    msFetch<PublicationJob>('/publications', { method: 'POST', body: JSON.stringify(input) }),
  bulkPublish: (input: { listing_ids: string[]; channel: MarketplaceChannel; action: string }) =>
    msFetch<PublicationJob[]>('/publications/bulk', { method: 'POST', body: JSON.stringify(input) }),
  retryPublication: (id: string) => msFetch<PublicationJob>(`/publications/${id}/retry`, { method: 'POST' }),
  retryQueue: () => msFetch<RetryTask[]>('/publications/retry-queue'),
  deadLetter: () => msFetch<DeadLetterEntry[]>('/publications/dead-letter'),

  listSync: () => msFetch<SyncJob[]>('/sync'),
  createSync: (input: { channel: MarketplaceChannel; sync_type: string }) =>
    msFetch<SyncJob>('/sync', { method: 'POST', body: JSON.stringify(input) }),
  fullSync: (channel: MarketplaceChannel) =>
    msFetch<SyncJob>('/sync/full', { method: 'POST', body: JSON.stringify({ channel }) }),
  incrementalSync: (channel: MarketplaceChannel) =>
    msFetch<SyncJob>('/sync/incremental', { method: 'POST', body: JSON.stringify({ channel }) }),

  listFeeds: () => msFetch<Feed[]>('/feeds'),
  createFeed: (input: { channel: MarketplaceChannel; feed_type: string; documents: Record<string, unknown>[] }) =>
    msFetch<Feed>('/feeds', { method: 'POST', body: JSON.stringify(input) }),
  replayFeed: (id: string) => msFetch<Feed>(`/feeds/${id}/replay`, { method: 'POST' }),

  listConnectors: () => msFetch<Connector[]>('/connectors'),
  connectorHealth: (id: string) => msFetch<{ healthy: boolean; latency_ms: number; message: string }>(`/connectors/${id}/health`),

  listWebhooks: () => msFetch<WebhookEvent[]>('/webhooks'),
};
